
import React, { useState, useRef, useEffect } from 'react';
import { Bot, X, Send, Sparkles, BrainCircuit, Activity, ShieldAlert, Map as MapIcon, Hammer } from 'lucide-react';
import { analyzeOperations } from '../services/geminiService';
import { Job, UserProfile, LocalRequirement } from '../types';

interface AiAssistantProps {
  jobs: Job[];
  workers: UserProfile[];
  globalRequirements: LocalRequirement[];
}

export const AiAssistant: React.FC<AiAssistantProps> = ({ jobs, workers, globalRequirements }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [messages, setMessages] = useState<Array<{ role: 'user' | 'assistant', content: string }>>([
    { role: 'assistant', content: "Hello! I'm your ArborLink Operations Assistant. I can help you match workers to jobs, check regional compliance, or recommend specialized machinery and rental services for complex removals. What's on your schedule today?" }
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, isOpen]);

  const handleSend = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!query.trim() || isLoading) return;

    const userMsg = query;
    setQuery('');
    setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setIsLoading(true);

    const result = await analyzeOperations(userMsg, { jobs, workers, globalRequirements });
    
    setMessages(prev => [...prev, { role: 'assistant', content: result }]);
    setIsLoading(false);
  };

  return (
    <div className="fixed bottom-24 right-6 md:bottom-8 md:right-8 z-[100]">
      {!isOpen ? (
        <button 
          onClick={() => setIsOpen(true)}
          className="w-16 h-16 bg-slate-900 text-white rounded-full shadow-2xl flex items-center justify-center hover:scale-110 transition-all active:scale-95 group"
        >
          <div className="absolute -top-1 -right-1 w-5 h-5 bg-emerald-500 rounded-full border-2 border-white animate-pulse"></div>
          <Bot className="w-8 h-8 group-hover:rotate-12 transition-transform" />
        </button>
      ) : (
        <div className="bg-white w-[90vw] md:w-[400px] h-[600px] rounded-[2.5rem] shadow-2xl border border-slate-200 flex flex-col overflow-hidden animate-in slide-in-from-bottom-8 duration-300">
          <div className="p-6 bg-slate-900 text-white flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-500 rounded-xl">
                <BrainCircuit className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-black text-sm tracking-tight">Operations Assistant</h3>
                <div className="flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                  <span className="text-[10px] font-black text-emerald-400 uppercase tracking-widest">Columbus Hub Online</span>
                </div>
              </div>
            </div>
            <button onClick={() => setIsOpen(false)} className="p-2 hover:bg-white/10 rounded-full transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-4 bg-slate-50/50">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] p-4 rounded-2xl text-sm font-medium leading-relaxed whitespace-pre-wrap ${
                  m.role === 'user' 
                    ? 'bg-emerald-600 text-white rounded-tr-none' 
                    : 'bg-white text-slate-800 shadow-sm border border-slate-100 rounded-tl-none'
                }`}>
                  {m.content}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100 flex gap-2 items-center">
                  <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-bounce"></div>
                  <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                  <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                </div>
              </div>
            )}
          </div>

          <div className="p-4 bg-white border-t border-slate-100">
            <div className="grid grid-cols-2 gap-2 mb-4">
              <button 
                onClick={() => { setQuery("Who is best for the Midtown project?"); }}
                className="text-[10px] font-black text-slate-500 bg-slate-50 hover:bg-slate-100 p-2 rounded-lg text-left truncate flex items-center gap-1.5"
              >
                <Activity className="w-3 h-3 text-emerald-500" /> Match Workers
              </button>
              <button 
                onClick={() => { setQuery("What equipment is needed for a complex removal over a house, and where can I rent it?"); }}
                className="text-[10px] font-black text-slate-500 bg-slate-50 hover:bg-slate-100 p-2 rounded-lg text-left truncate flex items-center gap-1.5"
              >
                <Hammer className="w-3 h-3 text-amber-500" /> Machinery Help
              </button>
            </div>
            <form onSubmit={handleSend} className="flex gap-2">
              <input 
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Ask about tools, rentals, or matching..."
                className="flex-1 bg-slate-50 border-none rounded-xl px-4 py-3 text-xs font-bold outline-none focus:ring-2 focus:ring-emerald-500 transition-all"
              />
              <button 
                type="submit"
                disabled={isLoading}
                className="p-3 bg-slate-900 text-white rounded-xl hover:bg-slate-800 disabled:opacity-50 active:scale-95 transition-all"
              >
                <Send className="w-4 h-4" />
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
